/*
  <mm-signature>: color swatches and add-to-bag for the home page signature product.
  Prices are formatted with the store's own money format so they match the rest of the site.
*/

class MmSignature extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-mm-signature-config]');
    if (!configElement) return;

    this.config = JSON.parse(configElement.textContent);
    this.variants = new Map(this.config.variants.map((variant) => [String(variant.id), variant]));
    this.image = this.querySelector('[data-mm-signature-image]');
    this.addButton = this.querySelector('[data-mm-signature-add]');
    this.addText = this.querySelector('[data-mm-signature-add-text]');
    this.errorElement = this.querySelector('[data-mm-signature-error]');
    this.variantId = this.addButton.dataset.variantId;
    this.busy = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    for (const swatch of this.querySelectorAll('[data-mm-signature-swatch]')) {
      swatch.addEventListener('change', () => this.selectVariant(swatch.value), { signal });
    }
    this.addButton.addEventListener('click', () => this.addToCart(), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  formatMoney(cents) {
    const format = (precision, thousands, decimal) => {
      const [whole, fraction = ''] = (cents / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const styles = {
      amount: () => format(2, ',', '.'),
      amount_no_decimals: () => format(0, ',', '.'),
      amount_with_comma_separator: () => format(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => format(0, '.', ','),
      amount_with_apostrophe_separator: () => format(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => format(0, ' ', '.'),
      amount_with_space_separator: () => format(2, ' ', ','),
      amount_with_period_and_space_separator: () => format(2, ' ', '.'),
    };
    return this.config.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, style) => (styles[style] || styles.amount)());
  }

  selectVariant(variantId) {
    const variant = this.variants.get(String(variantId));
    if (!variant) return;
    this.variantId = String(variant.id);

    const colorName = this.querySelector('[data-mm-signature-color]');
    if (colorName) colorName.textContent = variant.title;

    if (this.image && variant.image) {
      this.image.src = variant.image.src;
      this.image.srcset = variant.image.srcset;
      this.image.alt = variant.image.alt;
    }

    const price = this.querySelector('[data-mm-signature-price]');
    if (price) price.textContent = this.formatMoney(variant.price);
    const was = this.querySelector('[data-mm-signature-was]');
    if (was) {
      was.hidden = !(variant.compareAt > variant.price);
      was.textContent = this.formatMoney(variant.compareAt);
    }

    this.addButton.disabled = !variant.available;
    this.addText.textContent = variant.available ? this.config.strings.add : this.config.strings.soldOut;
    this.errorElement.textContent = '';
  }

  setBusy(busy) {
    this.busy = busy;
    this.addButton.setAttribute('aria-disabled', String(busy));
    this.addText.textContent = busy ? this.config.strings.adding : this.config.strings.add;
  }

  async addToCart() {
    if (this.busy) return;
    this.errorElement.textContent = '';
    this.setBusy(true);

    const drawer = document.querySelector('cart-drawer');
    const useDrawer = drawer && typeof drawer.renderContents === 'function';
    const body = { items: [{ id: Number(this.variantId), quantity: 1 }] };
    if (useDrawer) {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      drawer.setActiveElement?.(this.addButton);
    }

    try {
      const response = await fetch(this.config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data || data.status) {
        this.errorElement.textContent = (data && (data.description || data.message)) || this.config.strings.error;
        this.setBusy(false);
        return;
      }

      if (useDrawer) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        this.setBusy(false);
        return;
      }
      window.location.assign(this.config.cartUrl);
    } catch (error) {
      this.errorElement.textContent = this.config.strings.error;
      this.setBusy(false);
    }
  }
}

if (!customElements.get('mm-signature')) customElements.define('mm-signature', MmSignature);
